<template>
  <div>
    <div class="headline5 mb-3">
      {{ $t("modals.createWorkspace.companyDetails") }}
    </div>
    <v-card>
      <v-card-title>
        <v-row>
          <v-col class="mt-2 mb-2" :cols="8">
            <v-text-field
              v-model="workspace.companyName"
              type="text"
              data-testid="add-workspace-modal-company-name"
              :rules="companyNameRules"
              variant="outlined"
              :label="$t('modals.createWorkspace.companyName')"
              :aria-label="$t('modals.createWorkspace.companyName')"
              :placeholder="$t('modals.createWorkspace.companyName')"
            ></v-text-field>
            <v-text-field
              v-model="workspace.displayName"
              type="text"
              data-testid="add-workspace-modal-display-name"
              class="mt-4"
              :rules="displayNameRules"
              counter="30"
              :label="$t('modals.createWorkspace.displayName')"
              :aria-label="$t('modals.createWorkspace.displayName')"
              :placeholder="$t('modals.createWorkspace.displayName')"
              :hint="$t('modals.createWorkspace.displayNameHint')"
              persistent-hint
              variant="outlined"
            ></v-text-field>
            <v-text-field
              v-model="workspace.domain"
              type="text"
              data-testid="add-workspace-modal-domain"
              class="mt-4"
              :rules="domainRules"
              :placeholder="$t('modals.createWorkspace.domainPlaceholder')"
              :label="$t('modals.createWorkspace.domain')"
              :aria-label="$t('modals.createWorkspace.domain')"
              :hint="$t('modals.createWorkspace.domainHint')"
              persistent-hint
              variant="outlined"
            >
            </v-text-field>
            <v-autocomplete
              v-if="showParentWorkspaceInput"
              v-model="workspace.parentWorkspaceId"
              class="mt-4"
              :placeholder="$t('modals.createWorkspace.parentWorkspace')"
              :label="$t('modals.createWorkspace.parentWorkspace')"
              item-title="workspaceName"
              item-value="workspaceId"
              :hide-no-data="false"
              :no-filter="true"
              auto-select-first="exact"
              :rules="parentWorkspaceIdRules"
              :loading="parentWorkspacesLoading"
              :menu-props="{
                maxHeight: '250',
              }"
              :items="parentWorkspaces"
              variant="outlined"
              @update:search="$emit('parent-workspace-search', $event)"
            >
              <template #item="{ item, props }">
                <v-list-item v-bind="props" title="">
                  <v-list-item-title title="">
                    {{ `${item.raw.workspaceName} (${item.raw.workspaceId})` }}
                  </v-list-item-title>
                </v-list-item>
              </template>
              <template #append-item>
                <div v-intersect="onParentWorkspaceIntersect" />
              </template>
            </v-autocomplete>
            <v-text-field
              v-model="workspace.adminEmail"
              class="mt-4"
              type="text"
              data-testid="add-workspace-modal-admin-email"
              :rules="adminEmailRules"
              :label="$t('modals.createWorkspace.adminEmail')"
              :aria-label="$t('modals.createWorkspace.adminEmail')"
              :placeholder="$t('modals.createWorkspace.adminEmailPlaceholder')"
              :hint="$t('modals.createWorkspace.adminEmailHint')"
              persistent-hint
              variant="outlined"
            ></v-text-field>
            <template v-if="showLimits">
              <div class="subtitle2 mt-4 mb-2">
                {{ $t("modals.createWorkspace.userAndDeviceLimits") }}
              </div>
              <cui-workspace-limits-card
                v-model:limited="workspace.limited"
                v-model:limits="workspace.limits"
                :labels="limitsLabels"
                inline
              />
            </template>
          </v-col>
        </v-row>
      </v-card-title>
    </v-card>
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import { type CuiValidationRule, CuiWorkspaceLimitsCard } from "@coronet/cui";
import type { MspHierarchyItem, MspWorkspaceDialogItem } from "@/_store/msp/msp.module.ts";
import { useMspWorkspaceModal } from "@/composables/useMspWorkspaceModal";
import { useWorkspaceFormText } from "@/composables/useWorkspaceFormText";

/**
 * Company details step of the MSP create-workspace wizard. The dialog owns the item and the
 * parent-workspace search (a step remounts on every navigation); the step edits the item in place.
 */
export default defineComponent({
  components: { CuiWorkspaceLimitsCard },
  props: {
    workspace: {
      type: Object as PropType<MspWorkspaceDialogItem>,
      required: true,
    },
    showParentWorkspaceInput: {
      type: Boolean,
      required: true,
    },
    parentWorkspaces: {
      type: Array as PropType<MspHierarchyItem[]>,
      required: true,
    },
    parentWorkspacesLoading: {
      type: Boolean,
      required: true,
    },
    parentWorkspaceIdRules: {
      type: Array as PropType<CuiValidationRule[]>,
      required: true,
    },
    showLimits: {
      type: Boolean,
      required: true,
    },
  },
  emits: ["parent-workspace-search", "parent-workspace-intersect"],
  setup(_props, { emit }) {
    const { companyNameRules, displayNameRules, domainRules, adminEmailRules } =
      useMspWorkspaceModal();
    const { limitsLabels } = useWorkspaceFormText();

    const onParentWorkspaceIntersect = (
      isIntersecting: boolean,
      entries: IntersectionObserverEntry[]
    ) => emit("parent-workspace-intersect", isIntersecting, entries);

    return {
      companyNameRules,
      displayNameRules,
      domainRules,
      adminEmailRules,
      limitsLabels,
      onParentWorkspaceIntersect,
    };
  },
});
</script>

<style lang="scss" scoped>
// The dialog's `.stepper-step__wrapper .v-card` rule reaches only cards in its own template.
.v-card {
  border-radius: 8px !important;
}
</style>
